import { useState } from 'react';
import { formatMs, eventTone, EVENT_LABEL, outcomeLabel, toneClass, toText } from './event-model';

function Chip({ tone, children }) {
    return (
        <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset ${toneClass(tone)}`}>
            {children}
        </span>
    );
}

function Block({ children, muted, italic, danger, compact }) {
    if (children === undefined || children === null || children === '') return null;
    return (
        <div className={`mt-1 overflow-auto whitespace-pre-wrap break-words ${compact ? 'max-h-32' : 'max-h-60'} ${muted ? 'text-gray-500' : ''} ${italic ? 'italic' : ''} ${danger ? 'text-red-600' : ''}`}>
            {children}
        </div>
    );
}

function Json({ value, compact }) {
    const text = toText(value);
    if (!text) return null;
    return (
        <pre className={`mt-1 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-50 p-1.5 text-[11px] text-gray-600 ${compact ? 'max-h-32' : 'max-h-56'}`}>
            {text}
        </pre>
    );
}

const join = parts => parts.filter(Boolean).join(' · ');

/** The readable body of one event; anything unrecognised falls back to its data as JSON. */
function EventBody({ event, compact }) {
    const d = event.data || {};
    switch (event.type) {
        case 'input':
        case 'response':
            return <Block compact={compact}>{d.text}</Block>;
        case 'model_call':
            return (
                <>
                    <Block muted>
                        {join([
                            d.purpose,
                            d.inputTokens !== undefined && `${d.inputTokens} in / ${d.outputTokens || 0} out tokens`,
                            d.finishReason && d.finishReason !== 'stop' && d.finishReason !== 'tool_calls' && `finish: ${d.finishReason}`,
                            d.toolCalls?.length > 0 && `asked for ${d.toolCalls.join(', ')}`,
                        ])}
                    </Block>
                    <Block compact={compact}>{d.text}</Block>
                    <Block danger>{d.error}</Block>
                </>
            );
        case 'tool_call':
            return (
                <>
                    {d.title && <Block muted>{d.title}</Block>}
                    {d.arguments && Object.keys(d.arguments).length > 0 && <Json value={d.arguments} compact={compact} />}
                </>
            );
        case 'tool_response':
            return <Block muted={d.ok !== false} danger={d.ok === false} compact={compact}>{d.result}</Block>;
        case 'workflow':
        case 'workflow_step':
        case 'decision':
            return (
                <>
                    <Block muted>
                        {join([
                            d.workflow,
                            d.stateType,
                            d.decidedBy && `decided by ${d.decidedBy}`,
                            d.branch && `branch: ${d.branch}`,
                            d.next && `next: ${d.next}`,
                            d.waitsFor && `waiting for: ${d.waitsFor === true ? 'user input' : d.waitsFor}`,
                        ])}
                    </Block>
                    {d.slotsChanged && <Json value={d.slotsChanged} compact={compact} />}
                    <Block danger>{d.error}</Block>
                </>
            );
        case 'error':
            return <Block danger>{d.message}</Block>;
        case 'turn_end':
            return <Block muted>{join([outcomeLabel(d.outcome), d.reason])}</Block>;
        default:
            return Object.keys(d).length > 0 ? <Json value={d} compact={compact} /> : null;
    }
}

/**
 * One turn's events in order, each with its offset from the start of the turn.
 * "Show raw data" swaps every body for the span payload it came from.
 */
export default function EventList({ turn, compact = false }) {
    const [raw, setRaw] = useState(false);

    return (
        <div>
            <label className="mb-2 flex items-center gap-2 text-[11px] text-gray-500">
                <input type="checkbox" checked={raw} onChange={event => setRaw(event.target.checked)} /> Show raw data
            </label>
            <ol className="space-y-1.5">
                {turn.events.map(event => (
                    <li key={event.id} className={`grid gap-2 text-xs ${compact ? 'grid-cols-[3.5rem,1fr]' : 'grid-cols-[4.5rem,1fr] gap-3'}`}>
                        <span className="pt-0.5 text-right font-mono text-gray-400">+{formatMs(event.offsetMs)}</span>
                        <div className={`min-w-0 rounded-lg bg-gray-50 p-2 ${event.type === 'tool_response' ? 'ml-4' : ''}`}>
                            <div className="flex flex-wrap items-center gap-2">
                                <Chip tone={eventTone(event)}>{EVENT_LABEL[event.type] || event.type}</Chip>
                                {event.type === 'tool_call' && event.data?.kind && <Chip>{event.data.kind}</Chip>}
                                {event.type === 'tool_response' && (
                                    <Chip tone={event.data?.ok === false ? 'danger' : 'ok'}>{event.data?.ok === false ? 'failed' : 'ok'}</Chip>
                                )}
                                {event.type === 'turn_end' && <span className="text-gray-500">{event.data?.status}</span>}
                                {event.name && event.type !== 'input' && <span className="font-medium text-gray-800">{event.name}</span>}
                                {event.durationMs !== undefined && event.durationMs !== null && (
                                    <span className="text-gray-400">{formatMs(event.durationMs)}</span>
                                )}
                                {event.status === 'running' && <Chip tone="warn">still running</Chip>}
                            </div>
                            {raw ? <Json value={event.raw || event.data} compact={compact} /> : <EventBody event={event} compact={compact} />}
                        </div>
                    </li>
                ))}
            </ol>
        </div>
    );
}
